import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  OnInit,
  ViewChild,
  inject
} from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { saveAs } from 'file-saver';
import { each, isArray, isObject, map } from 'lodash';
import { forkJoin, from } from 'rxjs';
import { VendorForm } from 'src/app/classes/form-groups/vendor-form';
import { Address, License, User, Vendor } from 'src/app/classes/models';
import { CompanyComplianceType } from 'src/app/classes/models/company-compliance-type';
import { InsuranceAgent } from 'src/app/classes/models/insurance-agent';
import { InvitationContact } from 'src/app/classes/models/invitation-contact';
import { Owner } from 'src/app/classes/models/owner';
import { VendorInvite } from 'src/app/classes/models/vendor-invite';
import { VendorTrade } from 'src/app/classes/models/vendor-trade';
import { VendorTradeState } from 'src/app/classes/models/vendor-trade-state';
import { InsurancePolicyType } from 'src/app/interfaces/insurance-policy-type';
import { LocalizationInfo } from 'src/app/interfaces/localization-info';
import { SmallBusinessOption } from 'src/app/interfaces/models/small-business-option';
import { StateFull } from 'src/app/interfaces/state-full';
import { EditVendorAddressModalComponent } from 'src/app/modals/edit-vendor-address-modal/edit-vendor-address-modal.component';
import { SelectComplianceProgramModalComponent } from 'src/app/modals/select-compliance-program-modal/select-compliance-program-modal.component';
import { AuthService } from 'src/app/services/auth.service';
import { CompanyService } from 'src/app/services/company.service';
import { LicenseService } from 'src/app/services/license.service';
import { ToastService } from 'src/app/services/toast.service';
import { UtilityService } from 'src/app/services/utility.service';
import { VendorInviteService } from 'src/app/services/vendor-invite.service';
import { VendorService } from 'src/app/services/vendor.service';
import { companiesService } from 'src/app/shared/vive-api/companies.service';
import { CompanyOperationStateDto } from 'src/app/shared/vive-api/vive-api-models';

@Component({
  selector: 'app-vendor-information',
  templateUrl: './vendor-information.component.html',
  styleUrls: ['./vendor-information.component.less'],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false
})
export class VendorInformationComponent implements OnInit {
  private _cdr = inject(ChangeDetectorRef);

  constructor(
    private route: ActivatedRoute,
    private ToastService: ToastService,
    private VendorService: VendorService,
    private CompanyService: CompanyService,
    private UtilityService: UtilityService,
    private AuthService: AuthService,
    private VendorInviteService: VendorInviteService,
    private ModalService: NgbModal,
    private LicenseService: LicenseService
  ) {}

  @ViewChild('logoInput') LogoInputElementRef?: ElementRef<HTMLInputElement>;

  VendorFormGroup = new VendorForm();

  VendorId!: number;
  Vendor?: Vendor;
  CompanyId?: number;
  CompanyOperationStates: CompanyOperationStateDto[] = [];

  MailingAddress!: Address;
  StreetAddress!: Address;
  RemitToAddress!: Address;
  Owners: Owner[] = [];
  VendorTradeStates: VendorTradeState[] = [];
  VendorTrades: VendorTrade[] = [];
  InsuranceAgents: InsuranceAgent[] = [];
  SmallBusinessOptions: SmallBusinessOption[] = [];

  vendorTradeStatesConcatenated: string = '';
  tradesConcatenated: string = '';
  smallBusinessOptionsConcatenated: string = '';
  logoUrl?: string;

  // This generates a map from the Primary Trade License Requirements assigned to the Vendor's Trade States.
  // The map represents a 'State' to boolean value, where the boolean value represents whether all of the licenses
  // have been added for a state.
  stateToLicenseCompletionMap: { [key in StateFull]?: boolean } = {};

  localizationInfo!: LocalizationInfo;
  LoggedInUser?: User;

  inviteVendor() {
    if (!this.Vendor) {
      throw this.ToastService.addErrorToast(
        'Error',
        'Missing Vendor information.',
        { returnError: true }
      );
    }

    if (!this.LoggedInUser) {
      throw this.ToastService.addErrorToast(
        'Error',
        'Missing required user information.',
        { returnError: true }
      );
    }

    const { CompanyId } = this.LoggedInUser;

    if (!CompanyId) {
      throw this.ToastService.addErrorToast('Error', 'Missing Client Id.', {
        returnError: true
      });
    }

    const { id: VendorId, legalName, dba, ComplianceContact } = this.Vendor;
    if (!ComplianceContact) {
      throw this.ToastService.addErrorToast(
        'Error',
        'Missing Compliance Contact for invite.',
        { returnError: true }
      );
    }
    const { email } = ComplianceContact;

    const { componentInstance, result } = this.ModalService.open(
      SelectComplianceProgramModalComponent,
      { backdrop: 'static', size: 'md' }
    );

    Object.assign(componentInstance, { CompanyId });

    from(result).subscribe({
      next: (complianceTypeId: CompanyComplianceType) => {
        if (!complianceTypeId) {
          return;
        }

        const InvitationContactRecord = new InvitationContact({
          CompanyId,
          VendorId,
          legalName,
          dba,
          email
        });

        const VendorInviteRecord = new VendorInvite({
          CompanyId,
          inviteEmail: email,
          InvitationContact: InvitationContactRecord,
          CompanyComplianceTypeId: complianceTypeId as any
        });

        this.VendorInviteService.saveVendorInvite(VendorInviteRecord).subscribe(
          {
            next: () => {
              this.ToastService.addSuccessToast(
                'Success',
                'Invitation successfully sent!'
              );
              this._cdr.markForCheck();
            }
          }
        );
      }
    });
  }

  isLicenseRequirements() {
    return Object.keys(this.stateToLicenseCompletionMap).length > 0;
  }

  // When editing and the logo zone is clicked.
  logoClick() {
    if (!this.LogoInputElementRef) {
      throw this.ToastService.addErrorToast(
        'Error',
        'Missing logo input element.',
        { returnError: true }
      );
    }

    const { nativeElement } = this.LogoInputElementRef;
    nativeElement.click();
  }

  // Handles the selection of the logo file.
  logoSelection(event: Event) {
    if (!this.LogoInputElementRef) {
      throw this.ToastService.addErrorToast(
        'Error',
        'Missing logo input element.',
        { returnError: true }
      );
    }

    const { nativeElement } = this.LogoInputElementRef;
    const { files } = nativeElement;

    if (!files) {
      throw this.ToastService.addErrorToast(
        'Error',
        'Missing files attribute.',
        { returnError: true }
      );
    }

    if (files.length > 1) {
      throw this.ToastService.addErrorToast(
        'Error',
        'Cannot upload more than one file at once.',
        { returnError: true }
      );
    }

    const file = files[0];

    if (!file) {
      throw this.ToastService.addErrorToast(
        'Error',
        'Missing file information.',
        { returnError: true }
      );
    }

    const { name: fileName, size: fileSize, type: fileType } = file;

    if (!['image/jpeg', 'image/jpg', 'image/png'].includes(fileType)) {
      throw this.ToastService.addErrorToast(
        'Error',
        'File must be a JPEG or PNG.',
        { returnError: true }
      );
    }

    if (fileSize > Math.pow(2, 20) * 5) {
      throw this.ToastService.addErrorToast(
        'Error',
        'File size cannot be greater than 5MB.',
        { returnError: true }
      );
    }

    from(file.arrayBuffer()).subscribe({
      next: (FileArrayBuffer) => {
        const base64EncodedFileData =
          this.UtilityService.convertArrayBufferToBase64EncodedString(
            FileArrayBuffer
          );
        const fileURI = `data:${fileType};base64,${base64EncodedFileData}`;

        this.VendorService.saveVendor({
          id: this.VendorId,
          logo: fileURI
        }).subscribe({
          next: () => {
            this.VendorFormGroup.patchValue({ logo: fileURI });
            this._cdr.markForCheck();
          }
        });
      }
    });
  }

  // Fetches both data together to avoid callback hell and to populate correctly the states serviced
  fetchVendorAndCompanyData() {
    const vendorQuery = {
      SmallBusinessOption: JSON.stringify({
        options: { required: false }
      }),
      Address: JSON.stringify({
        options: { required: false }
      }),
      VendorTradeState: JSON.stringify({
        options: { required: false, duplicating: false, separate: true },
        SubNationalDivision: {
          options: { required: false, duplicating: false },
          PrimaryTradeLicenseRequirement: {
            options: { required: false, duplicating: false },
            isRequired: true,
            PrimaryTrade: {
              options: { required: false, duplicating: false },
              attributes: ['id', 'name']
            },
            VendorTrade: {
              options: { required: true, duplicating: false },
              VendorId: this.VendorId
            },
            License: {
              options: { required: false, duplicating: false },
              attributes: ['id'],
              VendorId: this.VendorId
            }
          }
        }
      }),
      VendorTrade: JSON.stringify({
        options: { required: false, separate: true },
        PrimaryTrade: {
          options: { required: false },
          attributes: ['id', 'name']
        }
      }),
      InsuranceAgent: JSON.stringify({
        options: { required: false }
      }),
      ComplianceContact: JSON.stringify({})
    };

    const companyQuery = {
      StreetAddress: JSON.stringify({}),
      MailingAddress: JSON.stringify({}),
      CompanyOperationState: JSON.stringify({
        SubNationalDivision: {}
      }),
      CompanyComplianceType: JSON.stringify({
        attributes: ['id', 'type']
      })
    };

    console.log('hey there');
    forkJoin({
      vendor: this.VendorService.getVendorV2(this.VendorId, vendorQuery),
      company: from(
        companiesService.findOneById({
          pathParams: {
            companyId: this.CompanyId!
          },
          queryParams: {
            includeAssociations: [
              {
                name: 'StreetAddress'
              },
              {
                name: 'MailingAddress'
              },
              {
                name: 'CompanyOperationStates'
              },
              {
                name: 'CompanyComplianceTypes'
              }
            ]
          }
        })
      )
    }).subscribe({
      next: ({ vendor, company }) => {
        // Process Company data
        if (this.CompanyId && !company) {
          throw this.ToastService.addErrorToast(
            'Error',
            'Missing Company information.',
            { returnError: true }
          );
        }
        if (company) {
          const { CompanyOperationStates } = company;
          if (isArray(CompanyOperationStates)) {
            this.CompanyOperationStates.push(...CompanyOperationStates);
          }
        }
        // Process Vendor data
        if (!vendor) {
          throw this.ToastService.addErrorToast(
            'Error',
            'Missing Vendor information.',
            { returnError: true }
          );
        }

        this.Vendor = vendor;

        const {
          id,
          logo,
          LogoFileId,
          country,
          SmallBusinessOptions,
          Addresses,
          Owners,
          VendorTradeStates,
          VendorTrades,
          InsuranceAgents
        } = this.Vendor;

        if (LogoFileId) {
          this.VendorService.getVendorLogoS3PresignedUrl(id!).subscribe({
            next: (s3Response) => {
              this.logoUrl = s3Response.presignedUrl;
              this._cdr.markForCheck();
            }
          });
        }

        this.VendorFormGroup.patchValue({ id, logo });

        if (!country) {
          throw this.ToastService.addErrorToast(
            'Error',
            'Missing Vendor country.',
            { returnError: true }
          );
        }

        this.localizationInfo =
          this.UtilityService.getLocalizedValuesMap(country);

        if (isArray(SmallBusinessOptions)) {
          this.SmallBusinessOptions.push(...SmallBusinessOptions);
          this.generateConcatenatedSmallBusinessOptions();
        }

        if (isArray(Addresses)) {
          Addresses.forEach((a) => {
            if (a.isStreet) {
              this.StreetAddress = a;
            }
            if (a.isMailing) {
              this.MailingAddress = a;
            }
            if (a.isRemitTo) {
              this.RemitToAddress = a;
            }
          });
        }

        if (isArray(Owners)) {
          this.Owners.push(...Owners);
        }

        if (isArray(VendorTradeStates)) {
          this.VendorTradeStates.push(...VendorTradeStates);
          this.populateStateToLicenseCompletionMap();
        }

        if (isArray(VendorTrades)) {
          this.VendorTrades.push(...VendorTrades);
          const { tradesConcatenated } =
            this.UtilityService.generateConcatenatedTradesAndTradeCategories(
              this.VendorTrades
            );
          this.tradesConcatenated = tradesConcatenated;
        }

        if (isArray(InsuranceAgents)) {
          this.InsuranceAgents.push(...InsuranceAgents);
        }

        this.generateConcatenatedVendorTradeStates();

        this._cdr.markForCheck();
      }
    });
  }

  // This filters the Vendor Trade States to only include those that match the Company's Operation States.
  generateConcatenatedVendorTradeStates() {
    const companyStateAbbreviations = new Set(
      map(this.CompanyOperationStates, (cos) => {
        const { SubNationalDivision } = cos;
        return SubNationalDivision?.abbreviation;
      }).filter((abbr) => abbr !== undefined)
    );

    // Filter and map vendor trade states that match company operation states
    const matchingStates = this.VendorTradeStates.filter((vts) => {
      const { SubNationalDivision } = vts;
      if (!SubNationalDivision) {
        return false;
      }
      const { abbreviation } = SubNationalDivision;
      if (!abbreviation) {
        return false;
      }
      return companyStateAbbreviations.has(abbreviation);
    }).map((vts) => {
      return vts.SubNationalDivision!.abbreviation!;
    });

    this.vendorTradeStatesConcatenated = matchingStates.join(', ');
  }

  generateConcatenatedSmallBusinessOptions() {
    this.smallBusinessOptionsConcatenated = map(
      this.SmallBusinessOptions,
      (sbo) => sbo.type
    ).join(', ');
  }

  populateStateToLicenseCompletionMap() {
    each(this.VendorTradeStates, (vts) => {
      const { SubNationalDivision } = vts;

      if (!isObject(SubNationalDivision)) {
        throw this.ToastService.addErrorToast(
          'Error',
          'Vendor Trade State does not have Sub National Division.',
          { returnError: true }
        );
      }

      const { PrimaryTradeLicenseRequirements } = SubNationalDivision;

      if (!Array.isArray(PrimaryTradeLicenseRequirements)) {
        throw this.ToastService.addErrorToast(
          'Error',
          'License Requirements were not correctly loaded.',
          { returnError: true }
        );
      }

      if (PrimaryTradeLicenseRequirements.length > 0) {
        this.stateToLicenseCompletionMap[SubNationalDivision.name!] = true;
      }

      each(PrimaryTradeLicenseRequirements, (ptlr) => {
        const { Licenses } = ptlr;
        if (!Licenses || (isArray(Licenses) && Licenses.length === 0)) {
          this.stateToLicenseCompletionMap[SubNationalDivision.name!] = false;
        }
      });
    });
  }

  generateHandledPoliciesText(agent: InsuranceAgent) {
    if (!agent) {
      throw this.ToastService.addErrorToast(
        'Error',
        'Missing insurance agent information.',
        { returnError: true }
      );
    }

    const insuranceAgentProviderFields: {
      'General Liability': 'isGLProvider';
      'Commercial Auto Liability': 'isAutoProvider';
      'Umbrella Liability': 'isUmbrellaProvider';
      "Worker's Compensation": 'isWorkersProvider';
      'Professional Liability': 'isProfessionalProvider';
    } = {
      'General Liability': 'isGLProvider',
      'Commercial Auto Liability': 'isAutoProvider',
      'Umbrella Liability': 'isUmbrellaProvider',
      "Worker's Compensation": 'isWorkersProvider',
      'Professional Liability': 'isProfessionalProvider'
    };

    const managedAgentPolicies: InsurancePolicyType[] = [];
    each(insuranceAgentProviderFields, (agentPolicyType, policyName) => {
      if (agent[agentPolicyType]) {
        managedAgentPolicies.push(policyName as InsurancePolicyType);
      }
    });

    return managedAgentPolicies.join(', ');
  }

  getNumberOfEmployeesLabel(numberOfEmployees: number) {
    const employeeQuantityToLabelMap: { [key: number]: string } = {
      0: '0 Employees',
      1: '1 Employee',
      2: '2 Employees',
      3: '3 Employees',
      4: '4 Employees',
      5: '5 - 10 Employees',
      10: '11 - 25 Employees',
      25: '26 - 50 Employees',
      50: '51 - 100 Employees',
      100: '101 - 500 Employees',
      500: '501+ Employees'
    };

    return employeeQuantityToLabelMap[numberOfEmployees] || '';
  }

  editAddresses() {
    const { componentInstance, result } = this.ModalService.open(
      EditVendorAddressModalComponent,
      { backdrop: 'static', size: 'xl' }
    );
    Object.assign(componentInstance, { VendorId: this.VendorId });

    from(result).subscribe({
      next: ({
        StreetAddress,
        MailingAddress,
        RemitToAddress
      }: {
        StreetAddress: Address;
        MailingAddress: Address;
        RemitToAddress: Address;
      }) => {
        Object.assign(this.StreetAddress, StreetAddress);
        Object.assign(this.MailingAddress, MailingAddress);
        Object.assign(this.RemitToAddress, RemitToAddress);
        this._cdr.markForCheck();
      }
    });
  }

  ngOnInit(): void {
    const paramMap = this.route.snapshot.paramMap;
    const VendorId = Number(paramMap.get('VendorId'));
    const CompanyId = Number(paramMap.get('CompanyId'));

    if (!VendorId) {
      throw this.ToastService.addErrorToast('Error', 'Missing Vendor Id.', {
        returnError: true
      });
    }

    this.VendorId = VendorId;
    this.CompanyId = CompanyId;

    this.LoggedInUser = this.AuthService.getUser();

    this.fetchVendorAndCompanyData();
  }

  downloadLicenseFile(element: any) {
    const { key } = element;

    const selectedVendorTradeState = this.VendorTradeStates.find(
      (vts) => vts.SubNationalDivision?.name === key
    );

    this.LicenseService.getLicense(
      this.UtilityService.stringifyObjectsInQueryObject({
        attributes: JSON.stringify(['id', 'licenseFile', 'licenseFileName']),
        status: {
          $or: [{ $eq: 'Archived' }, { $ne: 'Archived' }, { $eq: null }]
        },
        VendorId: this.VendorId,
        TradeLicense: {
          PrimaryTradeLicenseRequirement: {
            SubNationalDivisionId:
              selectedVendorTradeState?.SubNationalDivision?.id
          }
        },
        LicenseFile: {}
      })
    ).subscribe({
      next: (license: License) => {
        const { licenseFile, LicenseFile } = license;

        if (LicenseFile) {
          const { filename } = LicenseFile;
          this.LicenseService.getLicenseFileS3PresignedUrl(
            this.VendorId,
            license.id!
          ).subscribe({
            next: (s3Response) => {
              saveAs(s3Response.presignedUrl, filename);
              this._cdr.markForCheck();
            }
          });
        } else if (licenseFile) {
          saveAs(licenseFile, license.licenseFileName || 'license.pdf');
          this._cdr.markForCheck();
        } else {
          throw this.ToastService.addErrorToast(
            'Error',
            'License is missing file information.',
            { returnError: true }
          );
        }
      }
    });
  }
}
